import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface AdminSettings {
  monthlyPrice: string;
  lifetimePrice: string;
  stripeMonthlyLink: string;
  stripeLifetimeLink: string;
  loading: boolean;
}

const DEFAULT_SETTINGS: Omit<AdminSettings, 'loading'> = {
  monthlyPrice: '4.99',
  lifetimePrice: '37',
  stripeMonthlyLink: '',
  stripeLifetimeLink: '',
};

let cachedSettings: Omit<AdminSettings, 'loading'> | null = null;
let cacheTimestamp = 0;
const CACHE_TTL = 60_000; // 1 minute

export const useAdminSettings = (): AdminSettings => {
  const [settings, setSettings] = useState<Omit<AdminSettings, 'loading'>>(
    cachedSettings || DEFAULT_SETTINGS
  );
  const [loading, setLoading] = useState(!cachedSettings);

  useEffect(() => {
    const now = Date.now();
    if (cachedSettings && now - cacheTimestamp < CACHE_TTL) {
      setSettings(cachedSettings);
      setLoading(false);
      return;
    }

    const fetchSettings = async () => {
      try {
        const { data } = await supabase
          .from('admin_settings')
          .select('key, value')
          .in('key', ['monthly_price', 'lifetime_price', 'stripe_monthly_link', 'stripe_lifetime_link']);

        if (data) {
          const newSettings = { ...DEFAULT_SETTINGS };
          data.forEach((s) => {
            if (s.key === 'monthly_price') newSettings.monthlyPrice = s.value;
            if (s.key === 'lifetime_price') newSettings.lifetimePrice = s.value;
            if (s.key === 'stripe_monthly_link') newSettings.stripeMonthlyLink = s.value;
            if (s.key === 'stripe_lifetime_link') newSettings.stripeLifetimeLink = s.value;
          });
          cachedSettings = newSettings;
          cacheTimestamp = Date.now();
          setSettings(newSettings);
        }
      } catch (error) {
        console.error('Error fetching admin settings:', error);
      }
      setLoading(false);
    };

    fetchSettings();
  }, []);

  return { ...settings, loading };
};
